'use client';

import AppFooter from '@/components/layouts/AppFooter';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from '@/components/ui/pagination';
import { Button } from '@/components/ui/button';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import Link from 'next/link';
import { LogoRegister, LogoSlogan, LogoText } from '@/components/common';
import CommonNav from '@/components/layouts/CommonNav';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import Image from 'next/image';
import { Calendar, ArrowRight, Eye, Search } from 'lucide-react';
import { format } from 'date-fns';
import { LogoLinks } from '@/components/common/smartguy-logo';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';

const MIN_SEARCH_LENGTH = 3;

const formatViews = (number) => {
  const value = Number(number) || 0;
  if (value < 1000) return value.toString();
  if (value < 1_000_000) return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  if (value < 1_000_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  return (value / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
};

const formatTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;

  const months = Math.floor(days / 30);
  if (months < 12) {
    if (months === 1) return '1 month ago';
    return `${months} months ago`;
  }

  const years = Math.floor(months / 12);
  if (years === 1) return '1 year ago';
  return `${years} years ago`;
};

export default function CatholicsSpeaks({ videos = [], allVideos = [], meta }) {
  const router = useRouter();
  const [openDropdown, setOpenDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (query.length < MIN_SEARCH_LENGTH) {
      return [];
    }

    return allVideos.filter((video) => video.title.toLowerCase().includes(query));
  }, [allVideos, searchQuery]);

  const handleInputChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    setOpenDropdown(query.length >= 1);
  };

  const handleOptionSelect = (videoId) => {
    router.push(`/catholics-speaks/${videoId}`);
  };

  const buildHref = (page) => {
    return page > 1 ? `/catholics-speaks?page=${page}` : '/catholics-speaks';
  };

  return (
    <div className="h-full w-full px-4">
      <div className="space-y-4">
        <div className="flex flex-1 flex-col items-center justify-center py-4">
          <Link href="/" className="flex flex-col items-center justify-center">
            <div className="flex items-center">
              <LogoText className="text-3xl" />
              <LogoRegister />
            </div>
            <LogoSlogan className="me-4 text-center text-[.7rem]" />
          </Link>

          <LogoLinks />
        </div>
        <div>
          <CommonNav />
        </div>
      </div>

      <div className="container mx-auto space-y-4">
        <div className="mx-auto w-full max-w-xs sm:max-w-md">
          <div className="relative mt-3 mb-6 w-full">
            <div className="group relative flex items-center transition-all duration-300 hover:scale-[1.01]">
              <Search
                className="text-primary absolute top-1/2 left-4 z-20 h-4 w-4 -translate-y-1/2 transform transition-colors duration-300"
                aria-hidden="true"
              />
              <Input
                type="text"
                placeholder="Search Catholics Speaks videos…"
                className="focus-visible:ring-dark-blue z-10 w-full max-w-lg rounded-full py-6 pr-12 pl-11 text-lg focus-visible:ring-1"
                value={searchQuery}
                onChange={handleInputChange}
              />
            </div>

            <Popover open={openDropdown} onOpenChange={setOpenDropdown}>
              <PopoverTrigger asChild>
                <div />
              </PopoverTrigger>

              <PopoverContent
                align="start"
                sideOffset={8}
                className="mx-auto w-xs rounded-sm border-0 bg-white p-0 shadow-lg sm:w-[420px]"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <ScrollArea
                  className={`${searchResults.length <= 5 ? 'h-max' : 'h-[400px] sm:h-[400px]'}`}
                >
                  <>
                    {searchQuery.length >= MIN_SEARCH_LENGTH &&
                      searchResults.length > 0 &&
                      searchResults.map((video) => (
                        <div
                          key={video.videoId}
                          className="text-primary flex cursor-pointer justify-start gap-2 px-4 py-2 hover:bg-gray-200"
                          onClick={() => handleOptionSelect(video.videoId)}
                        >
                          {video.thumbnailUrl ? (
                            <div className="relative h-[80px] w-[120px] flex-shrink-0 overflow-hidden rounded-[4px]">
                              <Image
                                src={video.thumbnailUrl}
                                width={130}
                                height={80}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="relative h-[80px] w-[120px] flex-shrink-0 overflow-hidden rounded-[4px] bg-gray-200"></div>
                          )}

                          <div className="flex flex-1 flex-col gap-1">
                            <div
                              className="line-clamp-2 text-[.9rem] font-semibold"
                              style={{ lineHeight: '18px' }}
                            >
                              {video.title}
                            </div>
                            <div className="text-[.65rem]">
                              <span className="me-2 text-blue-800">
                                {formatViews(video.views)} views.{' '}
                              </span>
                              <span>{formatTime(video.publishedAt)}</span>
                            </div>
                          </div>
                        </div>
                      ))}

                    {searchQuery.length >= 1 && searchQuery.length < MIN_SEARCH_LENGTH && (
                      <p className="text-muted-foreground px-4 py-2 text-sm">
                        Type at least 3 characters to search.
                      </p>
                    )}

                    {searchQuery.length >= MIN_SEARCH_LENGTH && searchResults.length === 0 && (
                      <p className="px-4 py-2">
                        No videos found. Try searching with different keywords.
                      </p>
                    )}
                  </>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {videos.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {videos.map((video) => (
              <Card
                key={video.videoId}
                className="group col-span-1 w-full gap-1 p-0 pb-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <CardHeader className="p-0">
                  <Link href={`/catholics-speaks/${video.videoId}`}>
                    <div className="relative h-[220px] w-full overflow-hidden rounded-t-lg bg-gray-100">
                      {video.thumbnailUrl ? (
                        <Image
                          src={video.thumbnailUrl}
                          alt={video.title}
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                      ) : null}
                    </div>
                  </Link>
                </CardHeader>

                <CardContent>
                  <div className="text-muted-foreground mb-3 flex w-full items-center justify-between gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {video.publishedAt && format(new Date(video.publishedAt), 'dd MMM yyyy')}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <Eye />
                      <span>{formatViews(video.views)}</span>
                    </div>
                  </div>

                  <CardTitle className="group-hover:text-primary line-clamp-2 text-xl font-bold transition-colors">
                    <Link href={`/catholics-speaks/${video.videoId}`}>{video.title}</Link>
                  </CardTitle>

                  <CardDescription className="mt-2 line-clamp-3">
                    {video.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="flex items-center justify-end pt-2">
                  <Link
                    href={`/catholics-speaks/${video.videoId}`}
                    className="hover:text-primary flex items-center gap-1 text-sm font-semibold"
                  >
                    Read more
                    <ArrowRight className="relative top-[1px] h-4 w-4" />
                  </Link>
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground py-12 text-center">
            No Catholics Speaks videos are available right now.
          </p>
        )}

        {meta?.lastPage > 1 && (
          <div className="mt-5 block">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <Button
                    variant="ghost"
                    className="flex w-auto px-2 disabled:cursor-not-allowed"
                    disabled={meta.firstPage === meta.page}
                  >
                    <Link
                      href={buildHref(meta.firstPage)}
                      className="text-md flex items-center gap-1 text-gray-600"
                    >
                      <ChevronsLeft /> First
                    </Link>
                  </Button>
                </PaginationItem>

                <PaginationItem>
                  <Button
                    variant="ghost"
                    className="flex w-auto px-2 disabled:cursor-not-allowed"
                    disabled={!meta.hasPreviousPage}
                  >
                    <Link
                      href={buildHref(meta.page - 1)}
                      className="text-md flex items-center gap-1 text-gray-600"
                    >
                      <ChevronLeft /> Previous
                    </Link>
                  </Button>
                </PaginationItem>

                <PaginationItem>
                  <PaginationLink href="#" isActive>
                    {meta.page}
                  </PaginationLink>
                </PaginationItem>

                <PaginationItem>
                  <Button
                    variant="ghost"
                    className="flex w-auto px-2 disabled:cursor-not-allowed"
                    disabled={!meta.hasNextPage}
                  >
                    <Link
                      href={buildHref(meta.page + 1)}
                      className="text-md flex items-center gap-1 text-gray-600"
                    >
                      Next <ChevronRight />
                    </Link>
                  </Button>
                </PaginationItem>

                <PaginationItem>
                  <Button
                    variant="ghost"
                    className="flex w-auto px-2 disabled:cursor-not-allowed"
                    disabled={meta.lastPage === meta.page}
                  >
                    <Link
                      href={buildHref(meta.lastPage)}
                      className="text-md flex items-center gap-1 text-gray-600"
                    >
                      Last <ChevronsRight />
                    </Link>
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}

        <AppFooter />
      </div>
    </div>
  );
}
